import { NgIf } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { MenuOption } from '../../models/menu-option';

@Component({
    selector: 'app-menu-input',
    templateUrl: './menu-input.component.html',
    styleUrls: ['./menu-input.component.scss'],
    imports: [NgIf, FormsModule, ReactiveFormsModule]
})
export class MenuInputComponent implements OnInit, OnDestroy {
  #subscriptions: Subscription = new Subscription();
  search: UntypedFormControl = new UntypedFormControl('');
  singleSelect: UntypedFormControl = new UntypedFormControl('');

  /** The unique element identifier. */
  @Input() inputId?: string = 'menuInput';

  /** The text applied to the field label. */
  @Input() label?: string;

  /** The name of the element. */
  @Input() name?: string = 'menu-input-ctrl';

  /** The text for the placeholder. */
  @Input() placeholder?: string;

  /** Denotes whether to remove the search text. */
  @Input() resetSearch?: boolean;

  /** Denotes whether this component is a searchable menu. */
  @Input() isSearch?: boolean;

  /** The user's selected option. */
  @Input() selectedOption?: MenuOption;

  /** Denotes whether the form control is disabled and styling should be applied. */
  @Input() set isDisabled(value: boolean | undefined) {
    if (value) {
      this.search.disable();
      this.singleSelect.disable();
    } else {
      this.search.enable();
      this.singleSelect.enable();
    }
  }

  /** Emits the user's search text. */
  @Output() searchValueUpdated = new EventEmitter<string>();

  constructor() {}

  ngOnInit(): void {
    this.#subscriptions.add(this.#subscribeToSearchChanges());
  }

  ngOnDestroy(): void {
    this.#subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.#handleSearchResetChanges(changes);
    this.#handleSelectedOptionChanges(changes);
  }

  /**
   * Emits search value changes.
   */
  #subscribeToSearchChanges(): void {
    this.search.valueChanges.subscribe((value) => {
      this.searchValueUpdated.emit(value);
    });
  }

  /**
   * Sets the search input without emitting the event.
   * @param searchValue The users' search input.
   */
  #setSearchValue(searchValue: string | undefined): void {
    if (this.isSearch && !!searchValue) {
      this.search.setValue(searchValue, { emitEvent: false });
    } else {
      this.singleSelect.setValue(searchValue, { emitEvent: false });
    }
  }

  /**
   * Determines whether to reset search value.
   * @param changes SimpleChanges
   */
  #handleSearchResetChanges(changes: SimpleChanges): void {
    if (this.#checkChanges('resetSearch', changes)) {
      this.#setSearchValue('');
    }
  }

  /**
   * Determines whether to set the search value with the selected option value.
   * @param changes SimpleChanges
   */
  #handleSelectedOptionChanges(changes: SimpleChanges): void {
    if (this.#checkChanges('selectedOption', changes)) {
      this.#setSearchValue(this.selectedOption?.label);
    }
  }

  /**
   * Check the changes for the property name and whether the
   * current value is defined.
   * @param propertyName The input property to check for changes.
   * @param changes The simple changes.
   * @returns
   */
  #checkChanges(propertyName: string, changes: SimpleChanges): boolean {
    /*
      keep for development purposes...
      console.log(`app-menu-input #checkChanges: ${propertyName}: '${changes[propertyName]?.currentValue}'`);
    */
    return propertyName in changes && changes[propertyName]?.currentValue
      ? true
      : false;
  }
}
